/**
 * Meraki debranding store — list/create/restore network backups.
 *
 * Backups are scoped to a network. We cache the list per-network so the page
 * can switch network selections without re-fetching unnecessarily, while
 * still being able to force a refresh when the user creates a new backup.
 */
import { create } from "zustand";
import { get as apiGet, post as apiPost, ApiError } from "@/api/client";
import { pollCeleryTask } from "@/api/celery";
import {
  NetworkBackupListSchema,
  CeleryTaskSchema,
  type NetworkBackup,
  type CeleryProgress,
} from "@/api/schemas/meraki";
import { MOCK_NETWORK_BACKUPS } from "@/api/fixtures/meraki-backups";

interface State {
  /** Per-network backup list, keyed by networkId. */
  backupsByNetwork: Record<string, NetworkBackup[]>;
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;

  loadFor: (networkId: string) => Promise<void>;
  /** POST a new backup; returns the celery task id used to drive the progress bar. */
  createBackup: (networkId: string, merakiKeyId: string) => Promise<string | null>;
  /** POST a restore of an existing backup to a target network; returns taskId. */
  restoreBackup: (backupId: string, targetNetworkId: string) => Promise<string | null>;
  /** Reusable poll loop — drives any progress bar. */
  pollTask: (taskId: string, onProgress: (p: CeleryProgress) => void) => Promise<CeleryProgress>;
}

export const useMerakiDebranding = create<State>((set, get) => ({
  backupsByNetwork: {},
  status: "idle",
  error: null,

  loadFor: async (networkId) => {
    if (get().status === "loading") return;
    set({ status: "loading", error: null });
    try {
      const raw = await apiGet<unknown>("/meraki/debranding/network-backups/", {
        network_id: networkId,
      });
      const parsed = NetworkBackupListSchema.parse(raw);
      set((s) => ({
        backupsByNetwork: { ...s.backupsByNetwork, [networkId]: parsed },
        status: "ready",
      }));
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      const fallback = MOCK_NETWORK_BACKUPS.filter((b) => b.networkId === networkId);
      set((s) => ({
        backupsByNetwork: { ...s.backupsByNetwork, [networkId]: fallback },
        status: "error",
        error: `${message} — showing fixture data`,
      }));
    }
  },

  createBackup: async (networkId, merakiKeyId) => {
    const raw = await apiPost<unknown>("/meraki/debranding/network-backups/", {
      network_id: networkId,
      meraki_key_id: merakiKeyId,
    });
    const parsed = CeleryTaskSchema.safeParse(raw);
    return parsed.success ? parsed.data.taskId : null;
  },

  restoreBackup: async (backupId, targetNetworkId) => {
    const raw = await apiPost<unknown>(
      `/meraki/debranding/network-backups/${backupId}/restore/`,
      { target_network_id: targetNetworkId },
    );
    const parsed = CeleryTaskSchema.safeParse(raw);
    return parsed.success ? parsed.data.taskId : null;
  },

  pollTask: pollCeleryTask,
}));
